"use client";

import { Check, Circle, Lock, PauseCircle, PlayCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export type FlowStageStatus =
  | "complete"
  | "active"
  | "waiting"
  | "locked"
  | "action";

export type FlowStage = {
  id: string;
  label: string;
  status: FlowStageStatus;
  description?: string;
};

export type FlowProgressSegment = {
  id: string;
  label: string;
  /** Relative width of this segment in the journey. */
  weight: number;
  /** 0–1 how filled this segment is. */
  fill: number;
};

export type FlowJourneyProgress = {
  percentage: number;
  current: number;
  total: number;
  segments: FlowProgressSegment[];
};

const statusIcon = {
  complete: Check,
  active: PlayCircle,
  waiting: PauseCircle,
  locked: Lock,
  action: Circle,
};

export function FlowStageRail({
  stages,
  className,
}: {
  stages: FlowStage[];
  className?: string;
}) {
  return (
    <nav
      aria-label="Onboarding progress"
      className={cn("flex items-center gap-1", className)}
    >
      {stages.map((stage, index) => {
        const Icon = statusIcon[stage.status];
        return (
          <div key={stage.id} className="flex items-center gap-1">
            <div
              title={stage.description}
              className={cn(
                "flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-medium",
                stage.status === "active" &&
                  "bg-primary text-primary-foreground",
                stage.status === "complete" && "bg-success/10 text-success",
                stage.status === "action" && "bg-amber-500/10 text-amber-700",
                stage.status === "waiting" && "bg-muted text-muted-foreground",
                stage.status === "locked" &&
                  "bg-muted/60 text-muted-foreground",
              )}
              aria-current={stage.status === "active" ? "step" : undefined}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{stage.label}</span>
            </div>
            {index < stages.length - 1 ? (
              <span className="h-px w-3 bg-border" aria-hidden />
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

export function FlowQuestionProgress({
  current,
  total,
  section,
  estimate,
}: {
  current: number;
  total: number;
  section?: string;
  estimate?: string;
}) {
  const safeTotal = Math.max(total, 1);
  const percentage = Math.min(100, Math.round((current / safeTotal) * 100));
  const percentLabel = estimate ?? `${percentage}%`;
  return (
    <div className="w-full space-y-2">
      <div
        className={cn(
          "flex items-center gap-3 text-xs text-muted-foreground",
          section ? "justify-between" : "justify-end",
        )}
      >
        {section ? (
          <span className="truncate uppercase tracking-[0.12em]">
            {section}
          </span>
        ) : null}
        <span className="shrink-0 tabular-nums">{percentLabel}</span>
      </div>
      <Progress
        value={percentage}
        aria-label={section ?? `${percentage}% complete`}
        className="h-1"
      />
    </div>
  );
}

/**
 * Typeform-style segmented track: one bar per journey section, weighted by
 * how many steps that section contributes, filled as the respondent advances.
 */
export function FlowSegmentedProgress({
  segments,
  percentage,
  className,
}: {
  segments: FlowProgressSegment[];
  percentage: number;
  className?: string;
}) {
  if (segments.length === 0) return null;
  return (
    <div
      className={cn("flex w-full gap-1", className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percentage}
      aria-label={`${percentage}% complete`}
    >
      {segments.map((segment) => (
        <div
          key={segment.id}
          title={segment.label}
          className="relative h-full min-w-0 overflow-hidden rounded-full bg-border/70"
          style={{ flexGrow: Math.max(segment.weight, 1) }}
        >
          <div
            className="absolute inset-y-0 left-0 bg-foreground transition-[width] duration-300 ease-out"
            style={{
              width: `${Math.min(100, Math.max(0, segment.fill * 100))}%`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

/** Group visible questionnaire steps into weighted, progressively filled segments. */
export function buildJourneyProgress(input: {
  steps: Array<{ id: string; stage?: string; title?: string }>;
  currentStepId: string;
}): FlowJourneyProgress {
  const { steps, currentStepId } = input;
  const total = Math.max(steps.length, 1);
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStepId),
  );
  const percentage = Math.min(
    100,
    Math.round(((currentIndex + 1) / total) * 100),
  );

  type Group = {
    id: string;
    label: string;
    indices: number[];
  };
  const groups: Group[] = [];
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index]!;
    const label = step.stage?.trim() || step.title?.trim() || "Section";
    const id = step.stage?.trim() || step.id;
    const last = groups[groups.length - 1];
    if (last && last.id === id) {
      last.indices.push(index);
    } else {
      groups.push({ id, label, indices: [index] });
    }
  }

  const segments: FlowProgressSegment[] = groups.map((group) => {
    const start = group.indices[0]!;
    const end = group.indices[group.indices.length - 1]!;
    const length = end - start + 1;
    let fill = 0;
    if (currentIndex > end) fill = 1;
    else if (currentIndex >= start) {
      fill = (currentIndex - start + 1) / length;
    }
    return {
      id: group.id,
      label: group.label,
      weight: length,
      fill,
    };
  });

  return {
    percentage,
    current: currentIndex + 1,
    total,
    segments,
  };
}
